<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Invite User</div>
        </template>
        <template v-slot:subheader>
            <div v-if="props.hasContactGroups">
                Add a new User to your team. After creating, you can assign them to OUs and Groups.
            </div>
            <div v-else>Add a new User to your team.</div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert
                v-if="props.hasContactGroups && !model.id && model.role === 'admin'"
                color="info"
                :closeable="false"
                shadow
            >
                Administrators have access to all Organizational Units and Groups.
            </vx-alert>
            <vx-alert v-else-if="props.hasContactGroups && !model.id" :closeable="false" shadow>
                After adding, you will be redirected to assign OUs and Groups to this user.
            </vx-alert>
            <vx-alert v-if="willUseSso" color="info" :closeable="false" data-test="sso-invite-hint">
                They'll log in with SSO.
            </vx-alert>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label>Email</vx-label>
                    <vx-input
                        data-test="user-email"
                        type="email"
                        v-model="model.email"
                        size="lg"
                        :error="blockedMessage || displayFirstError(validate.email.$errors)"
                    >
                    </vx-input>
                </div>
                <div>
                    <vx-label>Role</vx-label>
                    <vx-selectable
                        v-model="model.role"
                        size="lg"
                        relative-dropdown
                        :reduce="(option) => option.value"
                        :options="
                            teamRolesOptions({
                                exclude: ['owner'],
                            })
                        "
                        :clearable="false"
                        :error="displayFirstError(validate.role.$errors)"
                    >
                    </vx-selectable>
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="updateVisible(false)"
                    type="button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    :loading="submitting"
                    :disabled="blocked"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Invite
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { computed, ref } from 'vue';
import { VxAlert, VxButton, VxInput, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { invitationsClient } from '~/services';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { teamRolesOptions } from '~/constants/roles';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useVerifiedEmailDomains } from '~/composables/useVerifiedEmailDomains';

const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: false,
    default: () => ({
        email: '',
        role: '',
    }),
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    isKiosk: {
        type: Boolean,
        default: false,
    },
    hasContactGroups: {
        type: Boolean,
        default: false,
    },
});

const { $externalResults, catchError } = useFormRequestHandler();

const validationRules = computed(() => ({
    email: {
        maxLength: maxLength(255),
        required: required,
        email: email,
    },
    role: {
        required: required,
    },
}));

const validate = useVuelidate(validationRules, model, { $externalResults });

const { blocked, blockedMessage, willUseSso } = useVerifiedEmailDomains(
    props.teamId,
    computed(() => model.value.email ?? '')
);

const emit = defineEmits(['invited']);
const submitting = ref(false);

const updateVisible = (isVisible) => {
    if (!model.value.id) {
        model.value.email = '';
        model.value.role = '';
    }

    validate.value.$reset();
    visible.value = isVisible;
};

const submit = async () => {
    if (blocked.value) {
        return;
    }

    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        if (props.hasContactGroups) {
            if (model.value.role === 'none') {
                sessionStorage.setItem('voxie-pending-invite-email', model.value.email);
                sessionStorage.setItem('voxie-pending-invite-role', model.value.role);

                window.location.assign(
                    props.isKiosk
                        ? `/kiosk/teams/${props.teamId}/invites/create`
                        : `/settings/teams/${props.teamId}/membership/invites/create`
                );
                return;
            }

            const response = await invitationsClient.sendInvite(props.teamId, {
                ...model.value,
            });

            if (model.value.role === 'admin') {
                toasted.success('New User invited successfully');
                emit('invited');
                updateVisible(false);
                return;
            }

            window.location.assign(
                props.isKiosk
                    ? `/kiosk/teams/${props.teamId}/invites/${response.data.id}/target-access`
                    : `/settings/teams/${props.teamId}/membership/invites/${response.data.id}/target-access`
            );
            return;
        }

        await invitationsClient.sendInvite(props.teamId, {
            ...model.value,
        });

        toasted.success('New User invited successfully');
        emit('invited');

        updateVisible(false);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
